import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';

import {
  getAccountSettingsRequest,
  getPlansRequest,
  getSubscriptionPreviewRequest,
  getSubscriptionRequest,
  reactivateSubscriptionRequest,
  resumeSubscriptionRequest,
  subscriptionCustomerPortalSessionRequest,
  subscriptionUpdatePaymentMethodRequest,
  subscriptionUpdateStatusRequest,
  subscriptionUpgradeRequest,
  validateCouponCodeRequest,
  revokePauseScheduledSubscriptionRequest,
} from './extra-actions';
import {
  AccountSetting,
  Plan,
  ReactivateResumeSubscriptionResponsePayload,
  Subscription,
  SubscriptionPreviewResponsePayload,
  ValidateCouponCodeResponse,
} from './utils/types';
import { ResponseSuccess } from '../../shared/types';

function mergeSubscriptionPreviewPayload(
  body: ResponseSuccess & { isError?: boolean; message?: string },
): SubscriptionPreviewResponsePayload | null {
  const preview = body.payload as
    | SubscriptionPreviewResponsePayload
    | undefined;
  if (!preview) return null;
  return {
    ...preview,
    isError: Boolean(preview.isError || body.isError),
    message: preview.message ?? body.message,
  };
}

type RequestState = {
  status: RequestStatus;
  message: string;
  error: any;
};

type State = {
  getSubscriptionRequest: RequestState;
  getPlansRequest: RequestState;
  getSubscriptionPreviewRequest: RequestState;
  subscriptionUpdateStatusRequest: RequestState;
  subscriptionUpgradeRequest: RequestState;
  subscriptionCustomerPortalSessionRequest: RequestState;
  subscriptionUpdatePaymentMethodRequest: RequestState;
  reactivateSubscriptionRequest: RequestState;
  resumeSubscriptionRequest: RequestState;
  validateCouponCodeRequest: RequestState;
  revokePauseScheduledSubscriptionRequest: RequestState;
  getAccountSettingsRequest: RequestState;

  plans: Plan[];
  subscription: Subscription;
  subscriptionPreview: SubscriptionPreviewResponsePayload;
  customerPortalSession: {
    url: string;
  };
  updatePaymentMethodTx: string;
  reactivateSubscriptionResponse: ReactivateResumeSubscriptionResponsePayload;

  validateCouponCodeResponse: ValidateCouponCodeResponse;
  accountSettings: AccountSetting[];
};

const initialState: State = {
  getSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getPlansRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getSubscriptionPreviewRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  subscriptionUpdateStatusRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  subscriptionUpgradeRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  subscriptionCustomerPortalSessionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  subscriptionUpdatePaymentMethodRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  reactivateSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  resumeSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  validateCouponCodeRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  revokePauseScheduledSubscriptionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  getAccountSettingsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },

  plans: [],
  subscription: null,
  subscriptionPreview: null,
  customerPortalSession: null,
  reactivateSubscriptionResponse: null,
  updatePaymentMethodTx: '',
  validateCouponCodeResponse: null,
  accountSettings: [],
};

const billingAndSubscriptionsSlice = createSlice({
  name: 'billingAndSubscriptions',
  initialState,
  reducers: {
    resetGetSubscriptionRequest: (state) => {
      state.getSubscriptionRequest = initialState.getSubscriptionRequest;
    },
    resetGetPlansRequest: (state) => {
      state.getPlansRequest = initialState.getPlansRequest;
    },
    resetGetSubscriptionPreviewRequest: (state) => {
      state.getSubscriptionPreviewRequest =
        initialState.getSubscriptionPreviewRequest;
    },
    resetSubscriptionPreviewState: (state) => {
      state.subscriptionPreview = initialState.subscriptionPreview;
    },
    resetSubscriptionUpdateStatusRequest: (state) => {
      state.subscriptionUpdateStatusRequest =
        initialState.subscriptionUpdateStatusRequest;
    },
    resetSubscriptionUpgradeRequest: (state) => {
      state.subscriptionUpgradeRequest =
        initialState.subscriptionUpgradeRequest;
    },
    resetSubscriptionCustomerPortalSessionRequest: (state) => {
      state.subscriptionCustomerPortalSessionRequest =
        initialState.subscriptionCustomerPortalSessionRequest;
    },
    resetSubscriptionUpdatePaymentMethodRequest: (state) => {
      state.subscriptionUpdatePaymentMethodRequest =
        initialState.subscriptionUpdatePaymentMethodRequest;
    },
    resetReactivateSubscriptionRequest: (state) => {
      state.reactivateSubscriptionRequest =
        initialState.reactivateSubscriptionRequest;
    },
    resetResumeSubscriptionRequest: (state) => {
      state.resumeSubscriptionRequest = initialState.resumeSubscriptionRequest;
    },
    resetValidateCouponCodeRequest: (state) => {
      state.validateCouponCodeRequest = initialState.validateCouponCodeRequest;
      state.validateCouponCodeResponse =
        initialState.validateCouponCodeResponse;
    },
    resetRevokePauseScheduledSubscriptionRequest: (state) => {
      state.revokePauseScheduledSubscriptionRequest =
        initialState.revokePauseScheduledSubscriptionRequest;
    },
    resetGetAccountSettingsRequest: (state) => {
      state.getAccountSettingsRequest = initialState.getAccountSettingsRequest;
    },
  },
  extraReducers: (builder) => {
    // Get Subscription
    builder.addCase(getSubscriptionRequest.pending, (state) => {
      state.getSubscriptionRequest.status = RequestStatus.Pending;
      state.getSubscriptionRequest.error = null;
    });
    builder.addCase(getSubscriptionRequest.fulfilled, (state, action) => {
      state.getSubscriptionRequest.status = RequestStatus.Succeeded;
      state.getSubscriptionRequest.message = action.payload.message;
      state.subscription = action.payload.payload;
    });
    builder.addCase(getSubscriptionRequest.rejected, (state, action) => {
      state.getSubscriptionRequest.status = RequestStatus.Failed;
      state.getSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Plans
    builder.addCase(getPlansRequest.pending, (state) => {
      state.getPlansRequest.status = RequestStatus.Pending;
      state.getPlansRequest.error = null;
    });
    builder.addCase(getPlansRequest.fulfilled, (state, action) => {
      state.getPlansRequest.status = RequestStatus.Succeeded;
      state.getPlansRequest.message = action.payload.message;
      state.plans = action.payload.payload.rows;
    });
    builder.addCase(getPlansRequest.rejected, (state, action) => {
      state.getPlansRequest.status = RequestStatus.Failed;
      state.getPlansRequest.error = !action.payload.isHandled && action.payload;
    });

    // Get Subscription Preview
    builder.addCase(getSubscriptionPreviewRequest.pending, (state) => {
      state.getSubscriptionPreviewRequest.status = RequestStatus.Pending;
      state.getSubscriptionPreviewRequest.error = null;
    });
    builder.addCase(
      getSubscriptionPreviewRequest.fulfilled,
      (state, action) => {
        state.getSubscriptionPreviewRequest.status = RequestStatus.Succeeded;
        state.getSubscriptionPreviewRequest.message = action.payload.message;
        state.subscriptionPreview = mergeSubscriptionPreviewPayload(
          action.payload as ResponseSuccess & {
            isError?: boolean;
            message?: string;
          },
        );
      },
    );
    builder.addCase(getSubscriptionPreviewRequest.rejected, (state, action) => {
      state.getSubscriptionPreviewRequest.status = RequestStatus.Failed;
      state.getSubscriptionPreviewRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Subscription Update Status
    builder.addCase(subscriptionUpdateStatusRequest.pending, (state) => {
      state.subscriptionUpdateStatusRequest.status = RequestStatus.Pending;
      state.subscriptionUpdateStatusRequest.error = null;
    });
    builder.addCase(
      subscriptionUpdateStatusRequest.fulfilled,
      (state, action) => {
        state.subscriptionUpdateStatusRequest.status = RequestStatus.Succeeded;
        state.subscriptionUpdateStatusRequest.message = action.payload.message;
      },
    );
    builder.addCase(
      subscriptionUpdateStatusRequest.rejected,
      (state, action) => {
        state.subscriptionUpdateStatusRequest.status = RequestStatus.Failed;
        state.subscriptionUpdateStatusRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Subscription Upgrade
    builder.addCase(subscriptionUpgradeRequest.pending, (state) => {
      state.subscriptionUpgradeRequest.status = RequestStatus.Pending;
      state.subscriptionUpgradeRequest.error = null;
    });
    builder.addCase(subscriptionUpgradeRequest.fulfilled, (state, action) => {
      state.subscriptionUpgradeRequest.status = RequestStatus.Succeeded;
      state.subscriptionUpgradeRequest.message = action.payload.message;
      state.subscriptionPreview = mergeSubscriptionPreviewPayload(
        action.payload as ResponseSuccess & {
          isError?: boolean;
          message?: string;
        },
      );
    });
    builder.addCase(subscriptionUpgradeRequest.rejected, (state, action) => {
      state.subscriptionUpgradeRequest.status = RequestStatus.Failed;
      state.subscriptionUpgradeRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Get Subscription Customer Portal Session
    builder.addCase(
      subscriptionCustomerPortalSessionRequest.pending,
      (state) => {
        state.subscriptionCustomerPortalSessionRequest.status =
          RequestStatus.Pending;
        state.subscriptionCustomerPortalSessionRequest.error = null;
      },
    );
    builder.addCase(
      subscriptionCustomerPortalSessionRequest.fulfilled,
      (state, action) => {
        state.subscriptionCustomerPortalSessionRequest.status =
          RequestStatus.Succeeded;
        state.subscriptionCustomerPortalSessionRequest.message =
          action.payload.message;
        state.customerPortalSession = action.payload.payload;
      },
    );
    builder.addCase(
      subscriptionCustomerPortalSessionRequest.rejected,
      (state, action) => {
        state.subscriptionCustomerPortalSessionRequest.status =
          RequestStatus.Failed;
        state.subscriptionCustomerPortalSessionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Subscription Update Payment Method
    builder.addCase(subscriptionUpdatePaymentMethodRequest.pending, (state) => {
      state.subscriptionUpdatePaymentMethodRequest.status =
        RequestStatus.Pending;
      state.subscriptionUpdatePaymentMethodRequest.error = null;
    });
    builder.addCase(
      subscriptionUpdatePaymentMethodRequest.fulfilled,
      (state, action) => {
        state.subscriptionUpdatePaymentMethodRequest.status =
          RequestStatus.Succeeded;
        state.subscriptionUpdatePaymentMethodRequest.message =
          action.payload.message;
        state.updatePaymentMethodTx =
          action.payload.payload?.transaction?.checkout?.transactionId ?? '';
      },
    );
    builder.addCase(
      subscriptionUpdatePaymentMethodRequest.rejected,
      (state, action) => {
        state.subscriptionUpdatePaymentMethodRequest.status =
          RequestStatus.Failed;
        state.subscriptionUpdatePaymentMethodRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Reactivate subscription request
    builder.addCase(reactivateSubscriptionRequest.pending, (state) => {
      state.reactivateSubscriptionRequest.status = RequestStatus.Pending;
      state.reactivateSubscriptionRequest.message = null;
      state.reactivateSubscriptionRequest.error = null;
    });
    builder.addCase(
      reactivateSubscriptionRequest.fulfilled,
      (state, action) => {
        state.reactivateSubscriptionRequest.status = RequestStatus.Succeeded;
        state.reactivateSubscriptionResponse = action.payload.payload;
        state.reactivateSubscriptionRequest.message = null;
        state.reactivateSubscriptionRequest.error = null;
      },
    );
    builder.addCase(reactivateSubscriptionRequest.rejected, (state, action) => {
      state.reactivateSubscriptionRequest.status = RequestStatus.Failed;
      state.reactivateSubscriptionRequest.message = action.payload.message;
      state.reactivateSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Resume subscription request
    builder.addCase(resumeSubscriptionRequest.pending, (state) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Pending;
      state.resumeSubscriptionRequest.message = null;
      state.resumeSubscriptionRequest.error = null;
    });
    builder.addCase(resumeSubscriptionRequest.fulfilled, (state, action) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Succeeded;
      state.resumeSubscriptionRequest.message = action.payload.payload.message;
    });
    builder.addCase(resumeSubscriptionRequest.rejected, (state, action) => {
      state.resumeSubscriptionRequest.status = RequestStatus.Failed;
      state.resumeSubscriptionRequest.message = action.payload.message;
      state.resumeSubscriptionRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Validate coupon code request
    builder.addCase(validateCouponCodeRequest.pending, (state) => {
      state.validateCouponCodeRequest.status = RequestStatus.Pending;
      state.validateCouponCodeRequest.message = null;
      state.validateCouponCodeRequest.error = null;
    });
    builder.addCase(validateCouponCodeRequest.fulfilled, (state, action) => {
      state.validateCouponCodeRequest.status = RequestStatus.Succeeded;
      state.validateCouponCodeResponse = action.payload.payload;
    });
    builder.addCase(validateCouponCodeRequest.rejected, (state, action) => {
      state.validateCouponCodeRequest.status = RequestStatus.Failed;
      state.validateCouponCodeRequest.message = action.payload.message;
      state.validateCouponCodeRequest.error =
        !action.payload.isHandled && action.payload;
    });

    // Revoke Pause Scheduled Subscription request
    builder.addCase(
      revokePauseScheduledSubscriptionRequest.pending,
      (state) => {
        state.revokePauseScheduledSubscriptionRequest.status =
          RequestStatus.Pending;
        state.revokePauseScheduledSubscriptionRequest.message = null;
        state.revokePauseScheduledSubscriptionRequest.error = null;
      },
    );
    builder.addCase(
      revokePauseScheduledSubscriptionRequest.fulfilled,
      (state, action) => {
        state.revokePauseScheduledSubscriptionRequest.status =
          RequestStatus.Succeeded;
        state.revokePauseScheduledSubscriptionRequest.message =
          action.payload.payload.message;
      },
    );
    builder.addCase(
      revokePauseScheduledSubscriptionRequest.rejected,
      (state, action) => {
        state.revokePauseScheduledSubscriptionRequest.status =
          RequestStatus.Failed;
        state.revokePauseScheduledSubscriptionRequest.error =
          !action.payload.isHandled && action.payload;
      },
    );

    // Get Account Settings
    builder.addCase(getAccountSettingsRequest.pending, (state) => {
      state.getAccountSettingsRequest.status = RequestStatus.Pending;
      state.getAccountSettingsRequest.error = null;
    });
    builder.addCase(getAccountSettingsRequest.fulfilled, (state, action) => {
      state.getAccountSettingsRequest.status = RequestStatus.Succeeded;
      state.getAccountSettingsRequest.message = action.payload.message;
      state.accountSettings = action.payload.payload;
    });
    builder.addCase(getAccountSettingsRequest.rejected, (state, action) => {
      state.getAccountSettingsRequest.status = RequestStatus.Failed;
      state.getAccountSettingsRequest.error =
        !action.payload.isHandled && action.payload;
    });
  },
});

export const {
  resetGetSubscriptionRequest,
  resetGetPlansRequest,
  resetGetSubscriptionPreviewRequest,
  resetSubscriptionPreviewState,
  resetSubscriptionUpdateStatusRequest,
  resetSubscriptionUpgradeRequest,
  resetSubscriptionCustomerPortalSessionRequest,
  resetSubscriptionUpdatePaymentMethodRequest,
  resetReactivateSubscriptionRequest,
  resetResumeSubscriptionRequest,
  resetValidateCouponCodeRequest,
  resetRevokePauseScheduledSubscriptionRequest,
  resetGetAccountSettingsRequest,
} = billingAndSubscriptionsSlice.actions;

export default billingAndSubscriptionsSlice.reducer;
